import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { BookOpen, ExternalLink, Search, Sparkles } from "lucide-react";
import { useMemo, useState } from "react";

import { ContentStatusBadge } from "~/components/admin/content-status-badge";
import { AdminDataTable } from "~/components/admin/data-table";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import {
  articleQueryKeys,
  articlesForReviewQueryOptions,
  type AdminArticleRow,
} from "~/lib/functions/cih/articles";
import {
  listSearchSchema,
  listSortDefaults,
  type SortColumn,
  sortListRows,
} from "~/lib/shared/admin-list-sort";
import { nextSortSearch } from "~/lib/shared/sort";

// Submitted is the review queue: the oldest submission comes first.
const ARTICLE_SORTS = ["title", "submitted"] as const;
type ArticleSort = (typeof ARTICLE_SORTS)[number];

const ARTICLE_SORT_DEFAULTS = listSortDefaults<ArticleSort>("submitted");

const ARTICLE_SORT_COLUMNS: Record<ArticleSort, SortColumn<AdminArticleRow>> = {
  title: { kind: "text", value: (row) => row.title },
  submitted: { kind: "date", value: (row) => row.submittedAt },
};

export const Route = createFileRoute("/admin/cih/articles/")({
  staticData: { title: "Articles" },
  validateSearch: listSearchSchema(ARTICLE_SORTS),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(
      articlesForReviewQueryOptions("in_review"),
    );
    return {};
  },
  component: ArticlesPage,
});

const STATUS_FILTERS = [
  { value: "in_review", label: "In Review" },
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
  { value: "archived", label: "Archived" },
  { value: "", label: "All" },
] as const;

// Suppress unused variable lint — query key is used to understand the cache key shape
void articleQueryKeys;

const ARTICLE_COLUMNS: ColumnDef<AdminArticleRow>[] = [
  {
    id: "title",
    header: "Title",
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <Link
          to="/admin/cih/articles/$articleId"
          params={{ articleId: row.original.id }}
          className="font-medium text-gray-900 hover:text-brand-600 hover:underline"
        >
          {row.original.title}
        </Link>
        {row.original.createdByNova && (
          <Badge variant="outline-primary" size="sm" title="Created by Nova">
            <Sparkles className="h-3 w-3" />
            Nova
          </Badge>
        )}
        {row.original.draftOf != null && (
          <Badge
            variant="outline-secondary"
            size="sm"
            title="Edit of a published article"
          >
            Edit
          </Badge>
        )}
      </div>
    ),
  },
  {
    id: "author",
    header: "Author",
    cell: ({ row }) =>
      row.original.coachUsername ? (
        <a
          href={`/${row.original.coachUsername}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-gray-700 hover:text-brand-600 hover:underline"
        >
          {row.original.coachFullName}
          <ExternalLink className="h-3 w-3" />
        </a>
      ) : (
        <span className="text-gray-700">
          {row.original.authorName}
          {row.original.authorTitle ? ` · ${row.original.authorTitle}` : ""}
        </span>
      ),
  },
  {
    id: "challenge",
    header: "Challenge",
    cell: ({ row }) =>
      row.original.challengeSlug ? (
        <a
          href={`/coaching-for/${row.original.challengeSlug}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-gray-700 hover:text-brand-600 hover:underline"
        >
          {row.original.challengeTitle}
          <ExternalLink className="h-3 w-3" />
        </a>
      ) : (
        <span className="text-gray-700">{row.original.challengeTitle}</span>
      ),
  },
  {
    id: "status",
    header: "Status",
    cell: ({ row }) => <ContentStatusBadge status={row.original.status} />,
  },
  {
    id: "submitted",
    header: "Submitted",
    cell: ({ row }) => (
      <span className="text-gray-500">
        {row.original.submittedAt
          ? new Date(row.original.submittedAt).toLocaleDateString()
          : "—"}
      </span>
    ),
  },
  {
    id: "view",
    header: "View",
    cell: ({ row }) =>
      row.original.status === "published" ? (
        <a
          href={`/articles/${row.original.slug}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-brand-600 hover:underline"
        >
          View
          <ExternalLink className="h-3 w-3" />
        </a>
      ) : (
        <span className="text-xs text-gray-400">—</span>
      ),
  },
];

function ArticlesPage() {
  const navigate = useNavigate({ from: Route.fullPath });
  const urlSearch = Route.useSearch();
  const sort = urlSearch.sort ?? ARTICLE_SORT_DEFAULTS.sort;
  const direction = urlSearch.dir ?? ARTICLE_SORT_DEFAULTS.direction(sort);
  const [statusFilter, setStatusFilter] = useState("in_review");

  const { data: articles, isLoading } = useQuery(
    articlesForReviewQueryOptions(statusFilter || undefined),
  );

  const rows = useMemo(
    (): AdminArticleRow[] =>
      sortListRows(
        articles ?? [],
        sort,
        direction,
        ARTICLE_SORT_COLUMNS,
        (a) => a.title,
      ),
    [articles, sort, direction],
  );

  const changeSort = (column: ArticleSort): void => {
    navigate({
      search: (previous) => ({
        ...previous,
        ...nextSortSearch({ sort, direction }, column, ARTICLE_SORT_DEFAULTS),
      }),
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="mb-1 text-2xl font-medium text-gray-900">
              Articles
            </h1>
            <p className="text-sm text-gray-500">Coach article review queue</p>
          </div>
          <Link to="/admin/cih/articles/new">
            <Button variant="primary" size="sm">
              New Article
            </Button>
          </Link>
        </div>

        {/* Status filter tabs */}
        <div className="mb-4 flex gap-1">
          {STATUS_FILTERS.map((f) => (
            <Button
              key={f.value}
              variant={statusFilter === f.value ? "primary" : "ghost"}
              size="sm"
              onClick={() => setStatusFilter(f.value)}
            >
              {f.label}
            </Button>
          ))}
        </div>

        {/* Table */}
        {isLoading ? (
          <Card className="gap-0 p-0">
            <div className="px-6 py-8 text-center text-sm text-gray-400">
              Loading...
            </div>
          </Card>
        ) : (
          <AdminDataTable
            data={rows}
            columns={ARTICLE_COLUMNS}
            getRowId={(row) => row.id}
            sorts={ARTICLE_SORTS}
            sort={sort}
            direction={direction}
            onSortChange={changeSort}
            emptyState={
              statusFilter ? (
                <EmptyState
                  size="sm"
                  icon={Search}
                  title="No matches"
                  description="No articles in this view. Try another status."
                />
              ) : (
                <EmptyState
                  size="sm"
                  icon={BookOpen}
                  title="No articles yet"
                  description="Articles appear here as coaches submit them for review."
                >
                  <Link to="/admin/cih/articles/new">
                    <Button variant="primary" size="sm">
                      New article
                    </Button>
                  </Link>
                </EmptyState>
              )
            }
          />
        )}
      </div>
    </div>
  );
}
